import { useState, useEffect } from 'react';

/**
 * Hook per il recupero dei pacchetti unificati da DevOps Feeds e AppVol.
 * Usa il nuovo endpoint /auth/reqLab/getUnifiedPackages.
 * Fallback: se l'endpoint unificato non e' disponibile, fetcha dai singoli feed.
 */
export default function useUnifiedPackages(baseImage, { appvolOnly = false } = {}) {
  const [packages, setPackages] = useState([]);
  const [bundlePackages, setBundlePackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!appvolOnly && (!baseImage?.feed || !baseImage?.feedBundle)) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchUnified() {
      setLoading(true);
      setError(null);

      try {
        // Tentativo con endpoint unificato
        const unifiedUrl = process.env.REACT_APP_URL_UNIFIED_PACKAGES;

        if (unifiedUrl) {
          const body = appvolOnly
            ? { appvolOnly: true, includeAppVol: true }
            : {
                feedName: baseImage.feed,
                feedBundleName: baseImage.feedBundle,
                includeAppVol: !!baseImage.hasAppVolAgent
              };

          const response = await fetch(unifiedUrl, {
            credentials: 'include',
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          });

          if (response.ok) {
            const data = await response.json();
            if (!cancelled) {
              setPackages(data.packages || []);
              setBundlePackages(data.bundlePackages || []);
              setCategories(data.categories || []);
            }
            return;
          }
        }

        // Fallback: fetch dai singoli feed DevOps (compatibilita' con vecchio endpoint)
        if (appvolOnly) {
          // No fallback per appvolOnly, serve l'endpoint unificato
          if (!cancelled) setError('Endpoint unificato non disponibile');
          return;
        }
        const repoUrl = process.env.REACT_APP_URL_REPO;
        const [feedResp, bundleResp] = await Promise.all([
          fetch(repoUrl + '?feedName=' + baseImage.feed + '&packageNameQuery=', {
            credentials: 'include',
            method: 'GET'
          }).then(r => r.json()).catch(() => []),
          fetch(repoUrl + '?feedName=' + baseImage.feedBundle + '&packageNameQuery=', {
            credentials: 'include',
            method: 'GET'
          }).then(r => r.json()).catch(() => [])
        ]);

        if (!cancelled) {
          // Normalizzazione client-side (fallback)
          const normalizedPkgs = (Array.isArray(feedResp) ? feedResp : []).map(el => ({
            id: `devops::${el.name}`,
            name: el.name,
            version: el.version || null,
            description: el.packageDescription || '',
            category: 'optional',
            source: 'devops',
            sourceDetail: { feed: baseImage.feed, feedBundle: null, applicationId: null },
            icon: null,
            locked: false,
            preSelected: false
          }));

          const normalizedBundle = (Array.isArray(bundleResp) ? bundleResp : []).map(el => ({
            id: `devops::${el.name}`,
            name: el.name,
            version: el.version || null,
            description: el.packageDescription || '',
            category: 'bundle',
            source: 'devops',
            sourceDetail: { feed: null, feedBundle: baseImage.feedBundle, applicationId: null },
            icon: null,
            locked: true,
            preSelected: true
          }));

          setPackages(normalizedPkgs);
          setBundlePackages(normalizedBundle);

          const catSet = new Set();
          [...normalizedPkgs, ...normalizedBundle].forEach(p => catSet.add(p.category));
          setCategories(Array.from(catSet));
        }

      } catch (err) {
        console.error('[useUnifiedPackages] Errore:', err);
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchUnified();

    return () => { cancelled = true; };
  }, [baseImage?.feed, baseImage?.feedBundle, baseImage?.hasAppVolAgent, appvolOnly]);

  return { packages, bundlePackages, categories, loading, error };
}
